import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'relative inline-flex cursor-pointer items-center justify-center gap-[6px] overflow-hidden whitespace-nowrap border-0 text-[13px] font-semibold text-white transition-[filter,box-shadow] duration-120 group disabled:pointer-events-none disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default:
          'h-[34px] rounded-lg bg-[linear-gradient(90deg,#f06f24_0%,#f8b117_100%)] px-4 text-white shadow-none hover:brightness-105 hover:shadow-[0_4px_12px_rgba(240,111,36,0.25)]',
        fullyBooked:
          'h-[34px] rounded-lg bg-[#d9d9d9] px-4 text-white shadow-none hover:bg-[#d9d9d9] hover:text-white',
        overlay: 'h-full w-full bg-transparent p-0',
        destructive: 'bg-red-600 text-white hover:bg-red-700',
        outline:
          'h-[45px] min-w-[76px] rounded-lg border border-[#d9d9d9] bg-white px-4 text-[#111111] shadow-none transition-[background-image,border-color,box-shadow] hover:!border-[#f06f24] duration-120 hover:bg-[linear-gradient(90deg,#f06f24_0%,#f8b117_100%)] hover:text-white hover:shadow-[0_4px_12px_rgba(240,111,36,0.25)]',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: undefined,
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  shine?: boolean;
  classBtn?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      classBtn,
      variant,
      size,
      asChild = false,
      shine = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size }), className)}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      >
        <span
          className={cn(
            'relative z-10 flex items-center justify-center',
            classBtn,
          )}
        >
          {children}
        </span>
        {shine && (
          <span className="absolute left-0 top-[7%] z-0 h-[86%] w-0 -skew-x-[20deg] bg-white opacity-0 shadow-[0_0_15px_3px_white] group-hover:animate-shine" />
        )}
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants };
